/**
 * SyncCore — platform-neutral Zustand + DO sync wiring.
 *
 * Owns the SyncContext plus the sync hooks. Consumers of the sync engine
 * call `useSync()` etc. — that works whether the provider tree is rooted at
 * the web `SyncProvider` (auth + the logo loader + Dexie) or a future RN wrapper.
 *
 * Platform-specific concerns (auth source, loading UI, storage init,
 * legacy-data migration endpoint, online detector) are injected via props
 * or module-level registrations — SyncCore itself imports nothing platform-
 * specific.
 */

import React, { createContext, useContext, useEffect } from 'react';

import { type AccountSeedState, shouldBlockOnAccountSeed } from '../account-seed';
import { type ConnectionState, connectionStateOf } from '../connection-state';
import { startDeckSummaryBridge } from '../deck-summary/store-bridge';
import type { SGLegacyMigrationClient } from '../internal/engine/study-group-do-adapter';
import {
  type LegacyMigrationClient,
  type SyncStatus,
  useIsOnline,
  type UserDoSyncResult,
  useUserDoSync,
} from '../internal/hook/use-user-do-sync';
import { useFlashkaStore } from '../internal/store/flashka-store';
import { StudyGroupSyncOrchestrator } from '../internal/study-group-orchestrator';
import { DeckCardsHydratorRegistrar } from '../provider/deck-cards-hydrator-registrar';
import { UserDoConnectionProvider } from '../provider/user-do-connection';
import { UserNotesDocProvider } from '../yjs/hooks';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const SyncContext = createContext<UserDoSyncResult | null>(null);

export type SyncLoadingInfo = {
  status: SyncStatus;
  /**
   * The first-sign-in seed. When it is `seeding` / `waiting` / `failed` the
   * overlay is the "bringing your account over" surface
   * (`accountSeedSurface`); otherwise it is the plain boot wait.
   */
  seed: AccountSeedState;
};

export type SyncCoreProps = {
  /** Signed-in user id, or null when signed out / not yet hydrated. */
  userId: string | null;
  /** Token fetcher, forwarded to PartySocket + Yjs WebSocket. */
  getToken: () => Promise<string | null>;
  /**
   * Platform-specific legacy-notes stash bridge. Web wires this to a
   * Dexie-backed stash; native leaves it undefined.
   */
  legacyMigrationClient?: LegacyMigrationClient;
  /**
   * Platform-specific STUDY-GROUP legacy-notes stash bridge — the SG
   * analogue of `legacyMigrationClient`. Threaded to the per-group SG
   * adapter so the owner's client can stash parked SG notes for seeding.
   * Undefined → bundle is ack'd + dropped (same as personal on native
   * before its stash was wired).
   */
  sgLegacyMigrationClient?: SGLegacyMigrationClient;
  /**
   * Optional one-shot per-user hydrator that loads anything stashed by a
   * legacy data path (e.g. Supabase notes parking lot on web). Web's
   * SyncProvider wires this to `hydrateLegacyStash`; RN passes nothing.
   * Lifted to a prop so this module doesn't statically import the
   * web-only legacy migration code.
   */
  hydrateLegacy?: (userId: string | null | undefined) => void | Promise<void>;
  /** Render prop for the platform's loading UI (the logo page loader on both platforms). */
  renderLoading?: (info: SyncLoadingInfo) => React.ReactNode;
  children: React.ReactNode;
};

export function SyncCore({
  userId,
  getToken,
  legacyMigrationClient,
  sgLegacyMigrationClient,
  hydrateLegacy,
  renderLoading,
  children,
}: SyncCoreProps) {
  // UserDoConnectionProvider owns the single y-partyserver WebSocket.
  // Both `useUserDoSync` (LWW JSON sync) and `UserNotesDocProvider`
  // (Yjs notes) consume that same connection via context — one socket
  // per signed-in user, not two.
  return (
    <UserDoConnectionProvider userId={userId} getToken={getToken}>
      {/* A background write that must read a deck's every card (a document
          landing in a cold deck) reaches the socket-bound rehydrate through
          this — see `internal/deck-cards-hydrator.ts`. */}
      <DeckCardsHydratorRegistrar />
      <SyncCoreInner
        userId={userId}
        legacyMigrationClient={legacyMigrationClient}
        hydrateLegacy={hydrateLegacy}
        renderLoading={renderLoading}>
        {children}
      </SyncCoreInner>
      {/*
        Study-group orchestrator — subscribes to `sync.groupMemberships`
        (personal per-user list on UserDo) and opens exactly one
        WebSocket to the per-group StudyGroupDo for each active
        membership. No-op when signed out.
      */}
      {userId ? (
        <StudyGroupSyncOrchestrator
          getToken={getToken}
          userId={userId}
          sgLegacyClient={sgLegacyMigrationClient}
        />
      ) : null}
    </UserDoConnectionProvider>
  );
}

function SyncCoreInner({
  userId,
  legacyMigrationClient,
  hydrateLegacy,
  renderLoading,
  children,
}: {
  userId: string | null;
  legacyMigrationClient?: LegacyMigrationClient;
  hydrateLegacy?: (userId: string | null | undefined) => void | Promise<void>;
  renderLoading?: (info: SyncLoadingInfo) => React.ReactNode;
  children: React.ReactNode;
}) {
  const sync = useUserDoSync(userId, legacyMigrationClient);

  // Per-deck study summaries: keep `deckSummaries` in step with the card
  // buckets for the life of the sync core, on both platforms. Independent of
  // the socket — the counts are derived from local rows and must be right
  // offline too. (`deck-summary/store-bridge.ts`.)
  useEffect(() => startDeckSummaryBridge(useFlashkaStore).stop, []);

  // Run any platform-supplied legacy hydration for this user. Idempotent
  // for the same userId, so re-running on re-renders is harmless.
  // Web wires this to `hydrateLegacyStash` (Supabase notes parking lot).
  // RN passes nothing.
  useEffect(() => {
    if (hydrateLegacy) void hydrateLegacy(userId);
  }, [userId, hydrateLegacy]);

  // The seed surface covers the app while a run writes the account, and —
  // with an empty store — while it waits or has failed: the adapter
  // re-asks by itself, and we don't want the user to see an empty app in
  // the interim. "Empty" must check deckProperties too, not just cards: on an
  // account whose decks are all archived (cold-deck cache) the `cards`
  // map is empty by design while the library is fully present locally —
  // blocking that store behind the loading overlay would turn a failed
  // sync into an unusable app instead of a local-data fallback. Same for
  // live groupMemberships: a member of deck-less study groups has zero
  // personal decks/cards, but their SG sockets (separate DOs) may be
  // perfectly healthy — a UserDo-only failure must not blank working
  // study-group pages behind this overlay.
  const seedStore = useFlashkaStore.getState();
  const storeHasData =
    Object.keys(seedStore.cards).length > 0 ||
    Object.keys(seedStore.deckProperties).length > 0 ||
    seedStore.groupMemberships.some((m) => !m.deletedTimeISO);

  const showLoading = shouldBlockOnAccountSeed(sync.seed, storeHasData);

  return (
    <SyncContext.Provider value={sync}>
      {/*
        UserNotesDocProvider consumes the shared WebSocket from
        UserDoConnectionProvider. When the user is signed out it still
        renders a `{ userDoc: null, synced: false }` context — no
        placeholder room, no unauthenticated connection attempt.
      */}
      <UserNotesDocProvider userId={userId}>
        {showLoading && renderLoading?.({ status: sync.syncStatus, seed: sync.seed })}
        {children}
      </UserNotesDocProvider>
    </SyncContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks — read from SyncContext
// ---------------------------------------------------------------------------

export function useSync(): UserDoSyncResult {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within a <SyncProvider>');
  return ctx;
}

/**
 * `useSync` for shells that also render signed OUT (no provider mounted):
 * `null` instead of a throw. Mobile's root onboarding gate reads
 * `hasCompletedInitialSync` through this.
 */
export function useSyncOptional(): UserDoSyncResult | null {
  return useContext(SyncContext);
}

export function useIsSyncing(): boolean {
  const { syncStatus } = useSync();
  return syncStatus === 'syncing' || syncStatus === 'connecting';
}

export function useSyncStatus(): SyncStatus {
  const { syncStatus } = useSync();
  return syncStatus;
}

/** The first-sign-in seed lifecycle — see `account-seed.ts`. */
export function useAccountSeed(): AccountSeedState {
  const { seed } = useSync();
  return seed;
}

/** True when the browser/device has network connectivity. */
export function useSyncIsOnline(): boolean {
  const { isOnline } = useSync();
  return isOnline;
}

/**
 * True once the DO sync has reached `ready` at least once since sign-in —
 * i.e. the Zustand store reflects server truth. Use this to gate UI
 * decisions that would flicker if computed from a still-empty store
 * (e.g. the onboarding-vs-dashboard choice on a returning user whose
 * Dexie is empty on a new device).
 */
export function useHasCompletedInitialSync(): boolean {
  const { hasCompletedInitialSync } = useSync();
  return hasCompletedInitialSync;
}

/**
 * Combined connection state: considers both network online status and
 * WebSocket sync status. Returns a user-friendly label.
 */
export function useConnectionState(): ConnectionState {
  const { isOnline, syncStatus, seed } = useSync();
  return connectionStateOf(isOnline, syncStatus, seed);
}

// Re-export the standalone hook so callers can read online status without
// a SyncProvider (e.g. in a top-level loading gate).
export { useIsOnline };
